import type { PricingRecalcOutput } from './hooks/usePricingRecalc';
import type { UsePricingDraftOutput, PricingFieldId } from './hooks/usePricingDraft';
import { formatCurrency } from './format';

/**
 * Pricing-v2 pending changes bar (QA Follow-up #5 Fix 6). Replaces the
 * shared `PendingChangesCard` mount on the pricing surface — that card is
 * still used by the user-profile form, but its orange Tailwind chrome +
 * bottom-right layout don't match the pricing design handoff.
 *
 * Two explicit modes, never mixed:
 *   - Dirty state (draft.dirtyCount > 0): count, KPI strip with Δ vs.
 *     baseline (hidden when only the Investor Loan # changed — it has no
 *     pricing impact), Review drawer with per-field rows, Save/Discard
 *     actions, blocked-reason strip. An earlier save still in flight keeps
 *     its status line and Retry/Cancel here, so new edits never hide it.
 *   - Status-only state (dirtyCount === 0 && activeGroup): status label
 *     only + Cancel/Retry (when eligible). Review/Revert/Save are NOT
 *     rendered in this mode — leaving them visible would be dead UI.
 *
 * Rendered text never contains Encompass field IDs (4794, NEWHUD.X1150,
 * LW.LO_REDUCTION, FV.*). Field labels come from a human-readable map.
 */

export interface PricingPendingBarProps {
  draft: UsePricingDraftOutput;
  /** Live recalc — includes active draft or pending submitted values. */
  recalc: PricingRecalcOutput;
  /** Authoritative baseline recalc from the current loan/pricing rows. */
  baselineRecalc: PricingRecalcOutput;
  onSave: () => void;
  onRetry?: () => void;
  onCancel?: () => void;
  /** Whether the review drawer is open. The bar is fully controlled by the
   *  workspace — single source of truth so the drawer state and the
   *  `[data-pricing-v2].review-open` padding class can never drift. */
  reviewOpen: boolean;
  /** Fires when the user requests opening/closing the review drawer. The
   *  workspace toggles its state and re-passes `reviewOpen` back down. */
  onReviewToggle: (open: boolean) => void;
  isSaving: boolean;
  isCancelling?: boolean;
  saveDisabled: boolean;
  saveDisabledReason?: string;
  activeGroup?: { id: string; status: string } | null;
  cancelEligible?: boolean;
  retryEligible?: boolean;
  /** Primary action label; candidate mode passes "Set Active & Save". */
  primaryLabel?: string;
  /** Replaces the generic waiting_unlock status line, e.g. naming who has the loan open. */
  waitingUnlockLabel?: string;
}

// Status labels — re-declared here (not imported from PendingChangesCard) so
// pricing-v2 stays independent of the profile-form card's internals.
const STATUS_LABEL: Record<string, string> = {
  pending: 'Saving…',
  pushing: 'Saving…',
  waiting_unlock: 'Waiting for Encompass file unlock',
  waiting_retry: 'Ready to retry Encompass sync',
  readback_pending: 'Sent to Encompass — waiting for readback',
  validated: 'Saved',
  cancelled: 'Cancelled',
  failed: 'Failed'
};

const FIELD_LABELS: Record<PricingFieldId, string> = {
  discount_point_percent: 'Discount points %',
  origination_fee_percent: 'Borrower-paid comp %',
  lender_credit_4794_dollars: 'Lender credit $',
  lo_comp_reduction_bps: 'LO comp reduction bps',
  investor_loan_number: 'Investor Loan #'
};

function formatSignedCurrency(n: number): string {
  if (Math.abs(n) < 0.005) {
    return '±$0';
  }
  return (n > 0 ? '+' : '−') + formatCurrency(Math.abs(n));
}

function deltaTone(delta: number): 'pos' | 'neg' | 'zero' {
  if (delta > 0.005) {
    return 'pos';
  }
  if (delta < -0.005) {
    return 'neg';
  }
  return 'zero';
}

export function PricingPendingBar({
  draft,
  recalc,
  baselineRecalc,
  onSave,
  onRetry,
  onCancel,
  reviewOpen,
  onReviewToggle,
  isSaving,
  isCancelling = false,
  saveDisabled,
  saveDisabledReason,
  activeGroup,
  cancelEligible,
  retryEligible,
  primaryLabel = 'Save changes',
  waitingUnlockLabel
}: PricingPendingBarProps) {
  // Status-only mode: committed state waiting for unlock/retry or briefly
  // showing success. The workspace also force-closes `reviewOpen` in this
  // mode; the bar mirrors that here so a stale `reviewOpen={true}` prop
  // never paints the drawer or the `.review-open` modifier.
  const statusOnly = draft.dirtyCount === 0 && activeGroup !== null && activeGroup !== undefined;
  const drawerOpen = !statusOnly && reviewOpen;

  // KPI deltas — derived against the authoritative baseline.
  const netDelta = recalc.netToLendwiseDollars - baselineRecalc.netToLendwiseDollars;
  const loCompDelta = recalc.loCompensationDollars - baselineRecalc.loCompensationDollars;
  const companyDelta = recalc.companyProfitDollars - baselineRecalc.companyProfitDollars;

  const pricingInputsDirty = draft.dirtyFields.some((id) => id !== 'investor_loan_number');

  const showCancel = Boolean(onCancel) && cancelEligible === true;
  const showRetry = Boolean(onRetry) && retryEligible === true;
  const statusLabel = activeGroup
    ? activeGroup.status === 'waiting_unlock' && waitingUnlockLabel
      ? waitingUnlockLabel
      : STATUS_LABEL[activeGroup.status] ?? activeGroup.status
    : null;

  // Root class list. `.on` matches the design's enter animation. `.blocked`
  // mutes the Save button when a field is invalid (parity with design).
  // Status-only mode has no Save button; its accent Retry must stay clickable.
  const rootClasses = [
    'pending-bar',
    'on',
    saveDisabled && !statusOnly ? 'blocked' : '',
    drawerOpen ? 'review-open' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses} role="region" aria-label="Pending pricing changes">
      {/* Review drawer expands upward with per-field rows. Hidden in status-only. */}
      {drawerOpen && (
        <div className="pending-list" role="list">
          {draft.dirtyFields.map((id) => {
            const value = draft.values[id];
            return (
              <div key={id} className="pending-row" role="listitem">
                <div className="pr-field">
                  <span className="pr-name">{FIELD_LABELS[id]}</span>
                </div>
                <div className="pr-diff">
                  <span className="pr-old">{value.previous || '—'}</span>
                  <span className="pr-arrow" aria-hidden>→</span>
                  <span className="pr-new">{value.value || '—'}</span>
                </div>
                <span className="pr-spacer" aria-hidden />
                <button
                  type="button"
                  className="pr-revert"
                  aria-label={`Revert ${FIELD_LABELS[id]}`}
                  onClick={() => draft.discardField(id)}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                    <path d="M3 3v5h5" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="pending-inner">
        {statusOnly ? (
          // Status-only: ONLY the status label + (conditional) Cancel Save.
          // No count, no KPI strip, no Review / Revert all / Save changes.
          <>
            <div className="pending-summary">
              <div className="pending-count">
                <span className="pc-label pc-status">{statusLabel}</span>
              </div>
            </div>
            {(showRetry || showCancel) && (
              <div className="pending-actions">
                {showRetry && (
                  <button
                    type="button"
                    className="btn accent sm"
                    onClick={onRetry}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Retrying…' : 'Retry Save'}
                  </button>
                )}
                {showCancel && (
                  <button
                    type="button"
                    className="btn sm"
                    onClick={onCancel}
                    disabled={isCancelling}
                  >
                    {isCancelling ? 'Cancelling…' : 'Cancel Save'}
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="pending-summary">
              <div className="pending-count">
                <span className="pc-num">{draft.dirtyCount}</span>
                <span className="pc-label">
                  {draft.dirtyCount === 1 ? 'change pending' : 'changes pending'}
                </span>
              </div>
              {pricingInputsDirty && (
                <div className="pending-kpis" aria-label="Projected impact">
                  <div className="pk">
                    <span className="pk-label">Net to Lendwise</span>
                    <span className="pk-value">{formatCurrency(recalc.netToLendwiseDollars)}</span>
                    <span className={`pk-delta ${deltaTone(netDelta)}`}>
                      {formatSignedCurrency(netDelta)}
                    </span>
                  </div>
                  <div className="pk">
                    <span className="pk-label">LO Comp</span>
                    <span className="pk-value">{formatCurrency(recalc.loCompensationDollars)}</span>
                    <span className={`pk-delta ${deltaTone(loCompDelta)}`}>
                      {formatSignedCurrency(loCompDelta)}
                    </span>
                  </div>
                  <div className="pk">
                    <span className="pk-label">Company Profit</span>
                    <span className="pk-value">{formatCurrency(recalc.companyProfitDollars)}</span>
                    <span className={`pk-delta ${deltaTone(companyDelta)}`}>
                      {formatSignedCurrency(companyDelta)}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="pending-actions">
              <button
                type="button"
                className="btn sm"
                onClick={() => onReviewToggle(!reviewOpen)}
                aria-expanded={drawerOpen}
              >
                <svg className="i" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M18 15l-6-6-6 6" />
                </svg>
                {drawerOpen ? 'Hide' : 'Review'}
              </button>
              <button
                type="button"
                className="btn sm"
                onClick={draft.discardAll}
                disabled={isSaving}
              >
                Revert all
              </button>
              <button
                type="button"
                className="btn accent sm"
                onClick={onSave}
                disabled={saveDisabled || isSaving}
                title={saveDisabled ? saveDisabledReason : undefined}
              >
                <svg className="i" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <path d="M17 21v-8H7v8M7 3v5h8" />
                </svg>
                {primaryLabel}
              </button>
              {showRetry && (
                <button
                  type="button"
                  className="btn sm"
                  onClick={onRetry}
                  disabled={isSaving}
                >
                  {isSaving ? 'Retrying…' : 'Retry Save'}
                </button>
              )}
              {showCancel && (
                <button
                  type="button"
                  className="btn sm pending-cancel"
                  onClick={onCancel}
                  disabled={isCancelling}
                >
                  {isCancelling ? 'Cancelling…' : 'Cancel Save'}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Blocked-reason strip — only in dirty state with a specific reason. */}
      {!statusOnly && saveDisabled && saveDisabledReason && (
        <div className="pending-block-tip" role="status">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span>
            {saveDisabledReason}
            {activeGroup && statusLabel ? ` ${statusLabel}` : ''}
          </span>
        </div>
      )}
    </div>
  );
}
